import React, { useCallback, useEffect, useRef, useState } from "react";
import { AuthContext } from "./AuthContext";
import { AuthState, AuthStrategy } from "./types";
import { createAuthStrategy, AuthStrategyType } from "./strategies";

interface AuthProviderProps {
  strategyType?: AuthStrategyType;
  children: React.ReactNode;
}

export function AuthProvider({ strategyType = "auth0", children }: AuthProviderProps) {
  const strategyRef = useRef<AuthStrategy>(createAuthStrategy(strategyType));

  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
  });

  useEffect(() => {
    const strategy = strategyRef.current;
    strategy
      .getUser()
      .then((user) => {
        setState({
          isAuthenticated: !!user,
          isLoading: false,
          user,
          error: null,
        });
      })
      .catch(() => {
        setState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: null,
        });
      });
  }, []);

  const login = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const result = await strategyRef.current.login();
      setState({
        isAuthenticated: true,
        isLoading: false,
        user: result.user,
        error: null,
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      }));
    }
  }, []);

  const logout = useCallback(async () => {
    await strategyRef.current.logout();
    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null,
    });
  }, []);

  const getToken = useCallback(async () => {
    return strategyRef.current.getToken();
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}
